import { generateConfigurationTemplate } from "pages/ConfigurationPage/configurationTemplate.ts"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useBrandsQuery } from "hooks/query/useBrandsQuery.ts"
import { ChevronDown } from "lucide-react"

interface ConfigurationFeaturesProps {
  data: any
  onChange: (data: any) => void
  onValid: (valid: boolean) => void
}

export const ConfigurationFeatures = ({ data, onChange }: ConfigurationFeaturesProps) => {
  const { t } = useTranslation()
  const { data: brands } = useBrandsQuery()
  const template = generateConfigurationTemplate(t, brands || [])
  const [showAdditional, setShowAdditional] = useState(false)

  const toggleFeature = (sectionKey: string, featureKey: string) => {
    const current: string[] = data.config[sectionKey] || []
    onChange({
      ...data,
      config: {
        ...data.config,
        [sectionKey]: current.includes(featureKey)
          ? current.filter((k: string) => k !== featureKey)
          : [...current, featureKey],
      },
    })
  }

  const updateSelect = (key: string, value: string) => {
    onChange({
      ...data,
      config: { ...data.config, [key]: value },
    })
  }

  return (
    <div className="grid grid-cols-[1fr_300px] gap-4">
      {/* Left: Feature flags + Categories */}
      <div className="space-y-4">
        {/* Active features */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">{template.activeFeatures.title}</CardTitle>
            <CardDescription className="text-xs">
              {template.activeFeatures.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {template.activeFeatures.items.map(({ key, title, description }) => (
                <div key={key} className="flex items-start gap-2.5">
                  <Checkbox
                    id={`feat-${key}`}
                    checked={data.config.features?.includes(key)}
                    onCheckedChange={() => toggleFeature("features", key)}
                    className="mt-0.5"
                  />
                  <div>
                    <Label htmlFor={`feat-${key}`} className="cursor-pointer text-sm">
                      {title}
                    </Label>
                    {description && <p className="text-xs text-muted-foreground">{description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Additional features (collapsible) */}
        <Card>
          <CardHeader className="pb-3">
            <button
              type="button"
              className="flex w-full items-center justify-between"
              onClick={() => setShowAdditional(!showAdditional)}
            >
              <div className="text-left">
                <CardTitle className="text-sm font-medium">
                  {template.additionalFeatures.title}
                </CardTitle>
                <CardDescription className="text-xs">
                  {template.additionalFeatures.description}
                </CardDescription>
              </div>
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  showAdditional ? "rotate-180" : ""
                }`}
              />
            </button>
          </CardHeader>
          {showAdditional && (
            <CardContent>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {template.additionalFeatures.items.map(({ key, title }) => (
                  <div key={key} className="flex items-center gap-2.5">
                    <Checkbox
                      id={`afeat-${key}`}
                      checked={data.config.features?.includes(key)}
                      onCheckedChange={() => toggleFeature("features", key)}
                    />
                    <Label htmlFor={`afeat-${key}`} className="cursor-pointer text-sm font-normal">
                      {title}
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Categories */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">{template.categories.title}</CardTitle>
            <CardDescription className="text-xs">{template.categories.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-x-6 gap-y-3">
              {template.categories.items.map(({ key, title }) => (
                <div key={key} className="flex items-center gap-2.5">
                  <Checkbox
                    id={`cat-${key}`}
                    checked={data.config.categories?.includes(key)}
                    onCheckedChange={() => toggleFeature("categories", key)}
                  />
                  <Label htmlFor={`cat-${key}`} className="cursor-pointer text-sm font-normal">
                    {title}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right: Select fields */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            {t("configurationTemplate.settingsTitle", "Settings")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {template.selects.map(({ key, title, options }) => (
            <div key={key} className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">{title}</Label>
              <Select value={data.config[key]} onValueChange={(value) => updateSelect(key, value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map(({ key: optKey, title: optTitle }) => (
                    <SelectItem key={optKey} value={optKey}>
                      {optTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
